import {
  createAdminClient,
  createBusinessClient,
  createCustomerClient,
  createMerchantClient,
} from '@beatq/sdk';
import { ApiError, csrfHeaders, refreshCsrfHeaders } from '../api/envelope.js';
import type { ApiErrorDetail } from '../api/envelope.js';
import { clearAccessToken, getAccessToken, setAccessToken } from './session.js';

export interface LoginResult {
  accessToken: string;
  refreshViaCookie: true;
}

/**
 * `POST /auth/login` nests the pair under `data.tokens` because it also returns
 * `principal`; `POST /auth/refresh` returns the pair flat under `data`.
 */
type LoginEnvelope = {
  data?: {
    accessToken?: string;
    tokens?: { accessToken?: string };
    mfaChallenge?: { challengeToken: string; type: string };
  };
  error?: { code: string; message: string; details?: ApiErrorDetail[] };
};

export class MfaRequiredError extends Error {
  constructor(
    readonly challengeToken: string,
    readonly challengeType: string,
  ) {
    super('Multi-factor authentication required');
    this.name = 'MfaRequiredError';
  }
}

export type ApiSurface = 'customer' | 'merchant' | 'admin' | 'business';

type ClientOptions = {
  baseUrl: string;
  credentials: RequestCredentials;
  headers: Record<string, string>;
  accessToken?: string;
};

function sdkOptions(baseUrl: string, accessToken?: string | null): ClientOptions {
  return {
    baseUrl,
    credentials: 'include',
    headers: csrfHeaders(),
    ...(accessToken ? { accessToken } : {}),
  };
}

function refreshOptions(baseUrl: string): ClientOptions {
  return {
    baseUrl,
    credentials: 'include',
    headers: { ...csrfHeaders(), ...refreshCsrfHeaders() },
  };
}

function readAccessToken(envelope: LoginEnvelope | undefined): string | undefined {
  return envelope?.data?.tokens?.accessToken ?? envelope?.data?.accessToken;
}

export async function loginWithPassword(
  baseUrl: string,
  surface: ApiSurface,
  email: string,
  password: string,
): Promise<LoginResult> {
  const body = { email, password } as never;
  const opts = sdkOptions(baseUrl);

  const { data, error, response } =
    surface === 'customer'
      ? await createCustomerClient(opts).POST('/customer/v1/auth/login', { body })
      : surface === 'merchant'
        ? await createMerchantClient(opts).POST('/merchant/v1/auth/login', { body })
        : surface === 'admin'
          ? await createAdminClient(opts).POST('/admin/v1/auth/login', { body })
          : await createBusinessClient(opts).POST('/business/v1/auth/login', { body });

  const envelope = (data ?? error) as LoginEnvelope | undefined;
  if (!response.ok || error) {
    throw new Error(envelope?.error?.message ?? 'Login failed');
  }

  const challenge = envelope?.data?.mfaChallenge;
  if (challenge) {
    throw new MfaRequiredError(challenge.challengeToken, challenge.type);
  }

  const accessToken = readAccessToken(envelope);
  if (!accessToken) {
    throw new Error(envelope?.error?.message ?? 'Login failed');
  }
  setAccessToken(accessToken);
  return { accessToken, refreshViaCookie: true };
}

export async function verifyLoginMfa(
  baseUrl: string,
  surface: ApiSurface,
  challengeToken: string,
  code: string,
): Promise<LoginResult> {
  const body = { challengeToken, code } as never;
  const opts = sdkOptions(baseUrl);
  const { data, error, response } =
    surface === 'customer'
      ? await createCustomerClient(opts).POST('/customer/v1/auth/mfa/verify', { body })
      : surface === 'merchant'
        ? await createMerchantClient(opts).POST('/merchant/v1/auth/mfa/verify', { body })
        : surface === 'admin'
          ? await createAdminClient(opts).POST('/admin/v1/auth/mfa/verify', { body })
          : await createBusinessClient(opts).POST('/business/v1/auth/mfa/verify', { body });

  const envelope = (data ?? error) as LoginEnvelope | undefined;
  if (!response.ok || error) {
    throw new Error(envelope?.error?.message ?? 'Authentication code could not be verified');
  }
  const accessToken = readAccessToken(envelope);
  if (!accessToken) throw new Error('MFA verification did not return an access token');
  setAccessToken(accessToken);
  return { accessToken, refreshViaCookie: true };
}

function authFailureMessage(
  response: Response,
  envelope: LoginEnvelope | undefined,
  fallback: string,
): string {
  if (!response.ok && (response.status === 502 || response.status === 503)) {
    return 'Cannot reach the API. Start the backend with `npm run dev` in beatq-eats-nodejs.';
  }
  return envelope?.error?.message ?? fallback;
}

/**
 * Merchant owner sign-up. The response carries a session so `MOD-005` can bind
 * the onboarding application to the new account (`SCR-MER-001`).
 */
export async function registerMerchantOwner(
  baseUrl: string,
  input: { email: string; password: string; displayName?: string },
): Promise<LoginResult> {
  let data: unknown;
  let error: unknown;
  let response: Response;
  try {
    ({ data, error, response } = await createMerchantClient(sdkOptions(baseUrl)).POST(
      '/merchant/v1/auth/register',
      { body: input as never },
    ));
  } catch (err) {
    if (err instanceof TypeError) {
      throw new Error(
        'Cannot reach the API. Start the backend with `npm run dev` in beatq-eats-nodejs.',
      );
    }
    throw err;
  }

  const envelope = (data ?? error) as LoginEnvelope | undefined;
  if (!response.ok || error) {
    const message = authFailureMessage(response, envelope, 'Registration failed');
    const code = envelope?.error?.code ?? 'ERR-UNKNOWN';
    throw new ApiError(code, message, undefined, envelope?.error?.details);
  }

  const accessToken = readAccessToken(envelope);
  if (!accessToken) {
    throw new ApiError(
      'ERR-UNKNOWN',
      authFailureMessage(response, envelope, 'Registration failed'),
    );
  }
  setAccessToken(accessToken);
  return { accessToken, refreshViaCookie: true };
}

export interface MerchantMembership {
  businessId: string;
  tradingName?: string;
  status?: string;
  verificationState?: string;
  isOwner?: boolean;
  branches?: Array<{ branchId: string; name?: string; status?: string }>;
}

/**
 * Scopes the signed-in merchant can switch into. Empty while onboarding is still
 * on the draft, which is how the console knows to send them to the checklist.
 */
export async function listMerchantMemberships(baseUrl: string): Promise<MerchantMembership[]> {
  const { data, error, response } = await createMerchantClient(
    sdkOptions(baseUrl, getAccessToken()),
  ).GET('/merchant/v1/auth/me/memberships');

  if (!response.ok || error) return [];
  const envelope = data as { data?: { businesses?: MerchantMembership[] } } | undefined;
  return envelope?.data?.businesses ?? [];
}

/** One in-flight refresh per surface — Strict Mode double-bootstrap must not rotate twice. */
const refreshInFlight = new Map<ApiSurface, Promise<string | null>>();

async function performRefresh(baseUrl: string, surface: ApiSurface): Promise<string | null> {
  const tokenBefore = getAccessToken();
  const opts = refreshOptions(baseUrl);
  const result =
    surface === 'customer'
      ? await createCustomerClient(opts).POST('/customer/v1/auth/refresh', { body: {} as never })
      : surface === 'merchant'
        ? await createMerchantClient(opts).POST('/merchant/v1/auth/refresh', { body: {} as never })
        : surface === 'admin'
          ? await createAdminClient(opts).POST('/admin/v1/auth/refresh', { body: {} as never })
          : await createBusinessClient(opts).POST('/business/v1/auth/refresh', {
              body: {} as never,
            });

  const { data, error, response } = result;
  const envelope = data as LoginEnvelope | undefined;
  if (!response.ok || error) {
    // Never wipe a token that landed during this attempt (login / parallel refresh winner).
    if (getAccessToken() === tokenBefore) {
      clearAccessToken();
    }
    return getAccessToken();
  }
  const accessToken = readAccessToken(envelope);
  if (accessToken) {
    setAccessToken(accessToken);
    return accessToken;
  }
  return getAccessToken();
}

export async function refreshSession(baseUrl: string, surface: ApiSurface): Promise<string | null> {
  const existing = refreshInFlight.get(surface);
  if (existing) return existing;

  const promise = performRefresh(baseUrl, surface).finally(() => {
    if (refreshInFlight.get(surface) === promise) {
      refreshInFlight.delete(surface);
    }
  });
  refreshInFlight.set(surface, promise);
  return promise;
}

export async function switchAuthContext(
  baseUrl: string,
  surface: ApiSurface,
  body: { businessId?: string; branchId?: string; businessAccountId?: string },
): Promise<string> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('Authentication required');
  }
  const opts = sdkOptions(baseUrl, token);
  const payload = body as never;

  const { data, error, response } =
    surface === 'customer'
      ? await createCustomerClient(opts).POST('/customer/v1/auth/context', { body: payload })
      : surface === 'merchant'
        ? await createMerchantClient(opts).POST('/merchant/v1/auth/context', { body: payload })
        : surface === 'admin'
          ? await createAdminClient(opts).POST('/admin/v1/auth/context', { body: payload })
          : await createBusinessClient(opts).POST('/business/v1/auth/context', { body: payload });

  const envelope = (data ?? error) as LoginEnvelope | undefined;
  if (!response.ok || error) {
    throw new Error(envelope?.error?.message ?? 'Failed to switch auth context');
  }
  const accessToken = readAccessToken(envelope);
  if (!accessToken) {
    throw new Error(envelope?.error?.message ?? 'Failed to switch auth context');
  }
  setAccessToken(accessToken);
  return accessToken;
}

export async function logoutSession(baseUrl: string, surface: ApiSurface): Promise<void> {
  const opts = sdkOptions(baseUrl, getAccessToken());
  try {
    if (surface === 'customer') {
      await createCustomerClient(opts).POST('/customer/v1/auth/logout', { body: {} as never });
    } else if (surface === 'merchant') {
      await createMerchantClient(opts).POST('/merchant/v1/auth/logout', { body: {} as never });
    } else if (surface === 'admin') {
      await createAdminClient(opts).POST('/admin/v1/auth/logout', { body: {} as never });
    } else {
      await createBusinessClient(opts).POST('/business/v1/auth/logout', { body: {} as never });
    }
  } finally {
    clearAccessToken();
  }
}

/** Clears in-memory token only — prefer logoutSession when online. */
export function logout(): void {
  clearAccessToken();
}
